import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import { isAuthenticated, setStoredSession } from '../services/auth';
import { Building2, ChevronRight, ArrowLeft, Mail, Lock, Eye, EyeOff, Hexagon } from 'lucide-react';
import './LoginPage.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [sessionMessage, setSessionMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [googleReady, setGoogleReady] = useState(Boolean(window.google?.accounts?.id));
  const googleButtonRef = useRef(null);
  const googleRenderedRef = useRef(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (location.state?.message) {
      setSessionMessage(location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || googleReady) {
      return;
    }

    const markReady = () => setGoogleReady(Boolean(window.google?.accounts?.id));
    const script = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
    if (script) {
      script.addEventListener('load', markReady);
      script.addEventListener('error', markReady);
      const timer = window.setTimeout(markReady, 1500);
      return () => {
        script.removeEventListener('load', markReady);
        script.removeEventListener('error', markReady);
        window.clearTimeout(timer);
      };
    }

    const googleScript = document.createElement('script');
    googleScript.src = 'https://accounts.google.com/gsi/client';
    googleScript.async = true;
    googleScript.defer = true;
    googleScript.onload = markReady;
    googleScript.onerror = markReady;
    document.head.appendChild(googleScript);

    return () => {
      googleScript.onload = null;
      googleScript.onerror = null;
    };
  }, [googleReady]);

  useEffect(() => {
    if (pendingSelection || !GOOGLE_CLIENT_ID || !googleReady || !window.google?.accounts?.id) {
      return;
    }
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (response) => {
        setLoading(true);
        setError('');
        try {
          const result = await authService.google({ credential: response.credential });
          if (result.data.data?.requiresOrganizationSelection) {
            setPendingSelection(result.data.data);
          } else {
            setStoredSession(result.data.data);
            navigate('/', { replace: true });
          }
        } catch (err) {
          setError(err.response?.data?.error || 'Google login failed');
        } finally {
          setLoading(false);
        }
      },
    });
    const target = googleButtonRef.current;
    if (target) {
      if (googleRenderedRef.current && target.childElementCount > 0) {
        return;
      }
      target.replaceChildren();
      window.google.accounts.id.renderButton(target, {
        theme: 'outline',
        size: 'large',
        width: 320,
        text: 'signin_with',
      });
      googleRenderedRef.current = true;
    }
  }, [pendingSelection, navigate, googleReady]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await authService.login({ email: formData.email, password: formData.password });
      if (result.data.data?.requiresOrganizationSelection) {
        setPendingSelection(result.data.data);
      } else {
        setStoredSession(result.data.data);
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrganization = async (organizationId) => {
    setLoading(true);
    setError('');
    try {
      const result = await authService.selectOrganization({
        selectionToken: pendingSelection.selectionToken,
        organizationId,
      });
      setStoredSession(result.data.data);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Organization selection failed');
    } finally {
      setLoading(false);
    }
  };

  const organizationChoices = (pendingSelection?.organizations || [])
    .filter(Boolean)
    .filter((org) => org.id || org._id);

  return (
    <div className="login-page">
      <div className="login-card">
        {/* Brand Logo Header */}
        <div className="login-logo-header">
          <div className="login-logo-icon">
            <Hexagon size={36} strokeWidth={1.5} color="#0f7a4f" />
          </div>
          <span className="login-logo-text">encye</span>
        </div>

        {!pendingSelection ? (
          <div className="login-card-header">
            <h1>Welcome Back</h1>
            <p>Sign in with your existing Encye account to use Agentic Article and RAG.</p>
          </div>
        ) : (
          <div className="login-card-header">
            <h1>Select Workspace</h1>
            <p>Choose an organization workspace to proceed to your dashboard.</p>
          </div>
        )}

        {error && <div className="login-error">{error}</div>}

        {pendingSelection ? (
          <div className="org-selection-container">
            <div className="org-choice-list">
              {organizationChoices.map((org) => (
                <button
                  key={org.id || org._id}
                  type="button"
                  className="org-choice-card"
                  disabled={loading}
                  onClick={() => handleSelectOrganization(org.id || org._id)}
                >
                  <div className="org-card-left">
                    <div className="org-card-icon-wrap">
                      <Building2 size={20} strokeWidth={2} />
                    </div>
                    <span className="org-card-name">{org.name || org.id || org._id}</span>
                  </div>
                  <ChevronRight size={18} strokeWidth={2.5} className="org-card-chevron" />
                </button>
              ))}
              {!organizationChoices.length && (
                <div className="login-error">No active organizations are available for this account.</div>
              )}
            </div>

            <button 
              type="button" 
              className="org-back-btn" 
              disabled={loading}
              onClick={() => setPendingSelection(null)}
            >
              <ArrowLeft size={16} strokeWidth={2.5} />
              Back to Sign In
            </button>
          </div>
        ) : (
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="login-field">
              <label htmlFor="email">Email Address</label>
              <div className="login-input-wrapper">
                <Mail className="login-input-icon" size={18} />
                <input 
                  id="email" 
                  name="email" 
                  type="email" 
                  placeholder="name@company.com" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                />
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="password">Password</label>
              <div className="login-input-wrapper">
                <Lock className="login-input-icon" size={18} />
                <input 
                  id="password" 
                  name="password" 
                  type={showPassword ? "text" : "password"} 
                  placeholder="••••••••" 
                  value={formData.password} 
                  onChange={handleChange} 
                  required 
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword(p => !p)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? <div className="login-spinner"></div> : 'Sign In'}
            </button>
          </form>
        )}
        {!pendingSelection && (
          <>
            <div className="login-divider"><span>or</span></div>
            <div className="login-google-panel">
              {GOOGLE_CLIENT_ID ? (
                <div className="google-login-shell">
                  <div id="google-login-button" className="google-login-button" ref={googleButtonRef} />
                  {!googleReady && <div className="login-google-loading">Preparing Google sign-in...</div>}
                </div>
              ) : (
                <div className="login-google-disabled">Set REACT_APP_GOOGLE_CLIENT_ID to enable Google login.</div>
              )}
            </div>
          </>
        )}
      </div>
      {sessionMessage && (
        <div className="login-session-modal-backdrop" role="presentation">
          <div
            className="login-session-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-session-modal-title"
          >
            <div className="login-session-hexagon" aria-hidden="true">
              <Hexagon size={52} strokeWidth={1.6} />
            </div>
            <h2 id="login-session-modal-title">{sessionMessage}</h2>
            <button
              type="button"
              className="login-session-modal-action"
              onClick={() => setSessionMessage('')}
              autoFocus
            >
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
